import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight } from "lucide-react";

interface Target {
  label: string;
  description: string;
  href: string;
  keywords?: string[];
  group: "Produtos" | "Soluções" | "Suporte" | "Conta";
}

const TARGETS: Target[] = [
  { label: "Certificado Pessoa Física (e-CPF)", description: "A1 e A3 para CPF — IR, e-CAC, Gov.br", href: "/certificado-pf", keywords: ["cpf", "ecpf", "pessoa fisica", "pf", "ir", "imposto"], group: "Produtos" },
  { label: "Certificado Pessoa Jurídica (e-CNPJ)", description: "A1 e A3 para CNPJ — NF-e, eSocial, SEFAZ", href: "/certificado-pj", keywords: ["cnpj", "ecnpj", "pessoa juridica", "pj", "empresa", "nfe", "esocial"], group: "Produtos" },
  { label: "Certificado MEI", description: "Para microempreendedor — NFS-e, DASN", href: "/certificado-mei", keywords: ["mei", "microempreendedor", "nfse", "dasn"], group: "Produtos" },
  { label: "Qual certificado eu preciso?", description: "Assistente interativo de escolha", href: "/qual-certificado", keywords: ["wizard", "ajuda", "dúvida", "escolher"], group: "Produtos" },
  { label: "Renovar certificado", description: "Renove antes do vencimento", href: "/renovar-certificado-digital", keywords: ["renovar", "renovação", "vencimento", "expirar"], group: "Produtos" },
  { label: "A1 ou A3: comparativo", description: "Qual tipo é ideal pra mim", href: "/certificado-digital/a1-ou-a3", keywords: ["a1", "a3", "comparar", "diferença"], group: "Produtos" },

  { label: "Biz Valida", description: "Antifraude, biometria e validação de identidade", href: "/biz-valida", keywords: ["biometria", "antifraude", "onboarding"], group: "Soluções" },
  { label: "Biz IA", description: "IA para empresas — automação e produtividade", href: "/biz-ia", keywords: ["inteligencia artificial", "ia", "automacao", "chatgpt"], group: "Soluções" },
  { label: "Biz Finance", description: "Crédito, seguros e previdência num só canal", href: "/biz-finance", keywords: ["credito", "seguro", "previdencia", "emprestimo"], group: "Soluções" },
  { label: "Biz Impulso", description: "Suporte contábil, jurídico e financeiro", href: "/biz-impulso", keywords: ["contabil", "juridico", "financeiro", "assessoria"], group: "Soluções" },

  { label: "Drivers e Instaladores", description: "Downloads oficiais para Windows, macOS e Linux", href: "/drivers-e-instaladores", keywords: ["driver", "instalador", "software", "download"], group: "Suporte" },
  { label: "Manuais", description: "Tutoriais passo a passo", href: "/manuais", keywords: ["manual", "tutorial", "como usar"], group: "Suporte" },
  { label: "Testar certificado", description: "Verifique se seu certificado está funcionando", href: "/testar-certificado", keywords: ["teste", "verificar", "funcionando"], group: "Suporte" },
  { label: "Emitir certificado", description: "Como emitir seu certificado após a compra", href: "/emitir-certificado", keywords: ["emitir", "emissao", "liberar"], group: "Suporte" },
  { label: "Contato e suporte", description: "Fale com nossa equipe por WhatsApp, telefone ou e-mail", href: "/contato-e-suporte", keywords: ["contato", "suporte", "whatsapp", "telefone", "email", "ajuda"], group: "Suporte" },
  { label: "Blog", description: "Artigos sobre certificado digital, NF-e e fiscal", href: "/blog", keywords: ["blog", "artigo", "post", "noticia"], group: "Suporte" },

  { label: "Meus pedidos", description: "Acompanhe seus pedidos e downloads", href: "/meus-pedidos", keywords: ["pedido", "compra", "acompanhar"], group: "Conta" },
  { label: "Minha conta", description: "Dados cadastrais e histórico", href: "/minha-conta", keywords: ["conta", "perfil", "dados"], group: "Conta" },
];

function norm(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const [query, setQuery] = useState("");

  // Reset query when closing
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const results = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return TARGETS;
    return TARGETS.filter((t) => {
      const hay = norm(`${t.label} ${t.description} ${(t.keywords ?? []).join(" ")}`);
      return hay.includes(q);
    });
  }, [query]);

  const grouped = useMemo(() => {
    const g: Record<string, Target[]> = {};
    for (const r of results) {
      g[r.group] = g[r.group] ?? [];
      g[r.group].push(r);
    }
    return g;
  }, [results]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Buscar no site</DialogTitle>
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar páginas, produtos, ajuda..."
            className="border-0 shadow-none focus-visible:ring-0 px-0"
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nada encontrado para "{query}". Tente outro termo ou{" "}
              <a href="/contato-e-suporte" className="text-primary underline">fale com o suporte</a>.
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="mb-2">
                <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {group}
                </p>
                {items.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-muted/60 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{item.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </a>
                ))}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
